// English copy. Typed against zh's MessageSchema —— missing keys fail the build.

import type { MessageSchema } from "./zh";

const messages: MessageSchema = {
  common: {
    appName: "Jzbro Cloud",
    appNameGlobal: "Starparks",
    confirm: "OK",
    cancel: "Cancel",
    loginRegister: "Log in / Sign up",
    userAgreement: "Terms of Service",
    privacyPolicy: "Privacy Policy",
    notBound: "Not set",
  },
  tabbar: {
    home: "Home",
    search: "Search",
    library: "Library",
    mine: "Me",
    badgeLogin: "Log in",
    badgeUpgrade: "Up",
  },
  login: {
    navTitle: "Log in",
    welcome: "Welcome to {brand}",
    subtitle: "Sign in with SMS, or use WeChat / QQ",
    subtitleGlobal: "Play instantly as a guest, or sign in with Google",
    mobileLabel: "Phone",
    mobilePlaceholder: "Phone number",
    codeLabel: "Code",
    codePlaceholder: "SMS code",
    getCode: "Get code",
    resendIn: "Resend in {sec}s",
    agreement:
      "An unregistered number creates an account automatically. By continuing you agree to the {userAgreement} and {privacyPolicy}",
    otherMethods: "Other ways to sign in",
    guest: "Continue as guest",
    wechat: "Sign in with WeChat",
    qq: "Sign in with QQ",
    google: "Continue with Google",
    telegram: "Continue with Telegram",
    facebook: "Continue with Facebook",
    wechatComingSoon: "WeChat sign-in coming soon",
    qqComingSoon: "QQ sign-in coming soon",
    googleComingSoon: "Google sign-in coming soon",
    googleFailed: "Google sign-in failed, please try again",
    telegramFailed: "Telegram login failed, please try again",
    facebookComingSoon: "Facebook sign-in coming soon",
    mobileInvalid: "Please enter a valid phone number",
    codeInvalid: "Please enter the code",
    codeSent: "Code sent",
  },
  loginPrompt: {
    defaultMessage: "Log in to see your games",
  },
  settings: {
    navTitle: "Settings",
    loginPrompt: "Log in to manage account settings",
    nickname: "Nickname",
    mobile: "Phone",
    controller: "Controller settings",
    controllerDesc: "Mouse sensitivity / right stick / button swap",
    about: "About",
    clearCache: "Clear local cache",
    language: "Language",
    editNickname: "Edit nickname",
    nicknamePlaceholder: "New nickname",
    nickEmpty: "Nickname can't be empty",
    nickTooLong: "Nickname is 16 characters max",
    updated: "Updated",
    cacheCleared: "Cleared. Please log in again",
    aboutTitle: "About {brand}",
    aboutVersion: "Web version v{version}",
    aboutBody:
      "Cloud gaming that lets you play AAA titles right in your browser, anywhere.\nAll game content is the property of its respective publishers.",
  },
  subscription: {
    choosePlan: "Choose a plan",
    included: "What's included",
    orderSummary: "Order summary",
    total: "Total",
    continue: "Continue",
    secureNote: "Secure checkout · One-time purchase",
    navTitle: "Upgrade",
    unlockTitle: "Unlock member perks",
    expired: "Expired",
    daysLeft: "{days} days left",
    validUntil: "Valid until {time}",
    tagline: "No queue, longer sessions, higher quality",
    perDay: "About {price}/day",
    recommended: "Best value",
    currentLevel: "Current plan",
    footer:
      "This is a one-time membership pass. The selected duration is added after payment is confirmed and will not renew automatically.",
    planVipMonth: "VIP Monthly",
    planSvipMonth: "SVIP Monthly",
    perkNoQueue: "Skip the queue",
    perkDailyFree: "60 free minutes daily",
    perkHd: "HD quality",
    perkAllVip: "All VIP perks",
    perkUnlimited: "Unlimited play time",
    perk4k: "4K / 60fps",
    perkSupport: "Priority support",
    cnPayComingSoon:
      "WeChat / Alipay checkout on web is coming soon — please use the app for now",
    intlPayComingSoon: "Stripe / PayPal checkout coming soon",
    payProcessing: "Complete the payment and keep this page open…",
    paySuccess: "Payment successful — membership activated",
    payFailed: "Payment failed, please try again",
    payCancelled: "Payment cancelled",
    telegramExtraTimeUnavailable: "Play-time purchases in Telegram will be available after the Stars version is added.",
    payUnconfirmed:
      "Payment not confirmed yet; if you've paid, it will be credited shortly",
    payPopupBlocked:
      "The payment window was blocked — please allow pop-ups and retry",
    loadPlansFailed: "Failed to load plans, please retry",
    retry: "Retry",
  },
  download: {
    nav: "Download App",
    title: "Get the Starparks app",
    subtitle: "Play Starparks on your phone and purchase membership in the app.",
    androidDescription: "The Android app is available on Google Play.",
    googlePlay: "Get it on Google Play",
    iosDescription: "The iOS app is on its way.",
    comingSoon: "Coming soon",
    membershipUnavailable: "Membership is not available yet",
    membershipAppHint: "Membership purchases are not available on this website. Please use the app to purchase a membership.",
    goToDownload: "Go to download page",
  },
  app: {
    backToDesktop: "Back to desktop",
  },
  titles: {
    download: "Download App",
    home: "Home",
    search: "Search",
    detail: "Game details",
    library: "Library",
    category: "Category",
    mine: "Me",
    myGames: "My games",
    reviews: "Reviews",
    gameVideos: "Gameplay videos",
    commentReplies: "Comment",
    saves: "Saves",
    subscription: "Upgrade",
    signIn: "Daily check-in",
    settings: "Settings",
    controlSettings: "Controller settings",
    login: "Log in",
    play: "Playing",
  },
  home: {
    searchPlaceholder: "Search games",
    sectionDefault: "Games",
    allGames: "All games",
    more: "More",
    empty: "Nothing here yet",
    latestOnline: "New releases",
    comingSoon: "Coming soon",
    subscribe: "Notify me",
    subscribed: "Subscribed",
    score: "Rating",
    subscribeSuccess: "Reserved — we'll notify you when it launches",
  },
  nav: {
    home: "Home",
    library: "Library",
    searchPlaceholder: "Search games",
    me: "Me",
    guest: "Guest",
    profile: "Profile",
    myGames: "My games",
    dailyCheckIn: "Daily check-in",
    renewMembership: "Renew membership",
    upgradeMembership: "Upgrade membership",
    settings: "Settings",
    switchToMobile: "Switch to mobile",
    logout: "Log out",
    logoutConfirm: "Are you sure you want to log out?",
    mobileVersion: "Mobile",
    login: "Log in",
  },
  library: {
    all: "All",
    noMore: "No more",
    loading: "Loading…",
    noData: "No data",
    noGames: "No games",
    noUpcoming: "No upcoming games",
    emptyCategory: "No games in this category",
  },
  mine: {
    notLoggedIn: "Not logged in",
    loginSyncTip: "Log in to sync membership, favorites and game history",
    loginSyncTipDesktop:
      "Log in to view your profile, membership, check-ins and game history",
    guest: "Guest",
    daysLeft: "{n} days left",
    expired: "Expired",
    renewBanner: "Your {level} expires in {n} days — renew for 30% off",
    membershipExpiredRenew: "Your membership has expired — renew now",
    membershipExpired: "Membership expired",
    membershipDaysLeft: "{n} days of membership left",
    gameTime: "Play time",
    minUnit: "min",
    dailyCheckIn: "Daily check-in",
    upgrade: "Upgrade",
    renew: "Renew",
    upgradeMembership: "Upgrade membership",
    renewMembership: "Renew membership",
    membership: "Membership",
    myCollection: "My favorites",
    collectedGames: "Saved games",
    recentlyPlayed: "Recently played",
    gameHistory: "Game history",
    settings: "Settings",
    switchToDesktop: "Switch to desktop",
    accountAndPrefs: "Account & preferences",
    claimGameTime: "Earn play time",
    bindPhone: "Bind phone number",
    bindPhoneDesc: "Upgrade to a full account and sync across devices",
    bindPhoneDescDesktop: "Upgrade to a full account, sync across devices",
    resetGuest: "Reset guest identity",
    resetGuestDesc: "Clear this guest account and start fresh",
    resetGuestDescDesktop: "Start fresh with a new identity",
    logout: "Log out",
    myGamesTitle: "My games",
    collectTab: "Favorites",
    loginViewCollect: "Log in to view your saved games",
    noCollect: "No saved games yet",
    loginViewHistory: "Log in to view your game history",
    noHistory: "No games played recently",
    weekMon: "Mon",
    weekTue: "Tue",
    weekWed: "Wed",
    weekThu: "Thu",
    weekFri: "Fri",
    weekSat: "Sat",
    weekSun: "Sun",
    alreadySignedToday: "Already checked in today",
    signSuccessReward: "Checked in! +{n} min",
    signSuccess: "Checked in",
    loginJoinCheckIn: "Log in to join daily check-in",
    claimed: "Claimed",
    unclaimed: "Missed",
    todayDone: "Today ✓",
    today: "Today",
    dayN: "Day {n}",
    signedToday: "Checked in today",
    signNow: "Check in now",
    checkInTip1: "Check in daily to earn free play time.",
    checkInTip2: "Missed days can be made up with coins on the make-up page.",
    loggedOut: "Logged out",
    resetGuestConfirm:
      "This clears the current guest account and starts fresh with a new identity. Purchased subscriptions, favorites and history will all be lost. Continue?",
    resetGuestConfirmBtn: "Reset",
    resetting: "Resetting…",
    resetDone: "Reset complete — please log in again",
  },
  reviews: {
    recommendRate: "recommend",
    tooFew: "Too few reviews",
    count: "{n} reviews",
    playFirst: "Play the game to leave a review",
    rateThis: "Played it? Leave a review",
    mine: "Mine",
    edit: "Edit",
    delete: "Delete",
    report: "Report",
    played: "Played {d}",
    contentEmpty: "Review can't be empty",
    editReview: "Edit review",
    editDone: "Updated",
    published: "Review posted",
    deleteTitle: "Delete review?",
    deleteConfirm: "This can't be undone. Delete this review?",
    deleted: "Deleted",
    reportTitle: "Report",
    reportConfirm: "Report this content? Our team will review it.",
    reported: "Reported",
    deleteReplyTitle: "Delete reply?",
    deleteReplyConfirm: "Delete this reply?",
    replyTo: "replied to",
    viewAllReplies: "View all {n} replies",
    loadMore: "Load more",
    allReplies: "All replies ({n})",
    commentDeleted: "This review was deleted",
    commentShielded: "This review was hidden",
    replyDeleted: "This reply was deleted",
    replyShielded: "This reply was hidden",
  },
  detail: {
    svipRequiredMsg: "This game requires SVIP to play",
    timeInsufficientMsg: "Insufficient game time. Please purchase game time first.",
    goBuy: "Buy Now",
    thinkAgain: "Think again",
    navTitle: "Game details",
    back: "Back",
    svipOnly: "SVIP only",
    vipOnly: "VIP only",
    freeMinutes: "{n} min free",
    saveManage: "Saves",
    saveManageDesc: "View / restore past saves and add notes",
    liveVideos: "Gameplay videos",
    more: "More",
    viewAll: "View all",
    viewAllCount: "View all {n}",
    intro: "About this game",
    screenshots: "Screenshots",
    controlMethod: "Controls: ",
    playerReviews: "Reviews",
    commentCount: "{n}",
    recommend: "Recommends",
    notRecommend: "Not recommended",
    similarGames: "Similar games",
    collect: "Favorite",
    collected: "Favorited",
    collectStar: "☆ Favorite",
    collectedStar: "★ Favorited",
    unfavorited: "Removed from favorites",
    entering: "Entering…",
    unlockSvip: "Unlock with SVIP",
    unlockVip: "Unlock with VIP",
    startGame: "Play",
    reviewCount: "{n} reviews",
    sortHot: "Top",
    sortNew: "Newest",
    noMore: "No more",
    playedHours: "Played {n} hrs",
    report: "Report",
    reportTitle: "Report this comment?",
    reportMessage: "Our moderators will review it. Report?",
    reported: "Reported",
    noReviews: "No reviews yet — be the first",
    writeReview: "Write a review",
    reviewPlaceholder: "Share your experience… (at least 5 characters)",
    reviewMinLength: "Review must be at least 5 characters",
    reviewSubmitted: "Review submitted",
    publish: "Publish",
    commentDetail: "Comment",
    replyCount: "{n} replies",
    noReplies: "No replies yet — be the first",
    reply: "Reply",
    replyToUser: "Reply to {'@'}{name}",
    saySomething: "Say something…",
    send: "Send",
    sent: "Sent",
    videoCount: "{n} videos",
    watchCount: "{k}k views",
    noVideos: "No videos yet",
    videoUnavailable: "This video can't be played right now",
    videoUnavailableDesc: "It may be an old clip that's been taken down — try another",
  },
  search: {
    placeholder: "Enter a game name",
    emptyHint: "Search by game name or pinyin",
    history: "Recent searches",
    clear: "Clear",
    noResults: "No games found",
  },
  misc: {
    savesTitle: "Game Saves",
    savesTitleNamed: "{name} - Saves",
    savesLoginPrompt: "Log in to view your game saves",
    savesActiveHeading: "Active save",
    savesUnnamed: "Unnamed save",
    savesEditRemark: "Rename",
    savesEmpty: "No cloud saves yet",
    savesEmptyHint: "Saves made in-game sync here automatically",
    savesHistory: "History",
    savesCount: "{n} saves",
    savesCurrent: "Current",
    savesCorrupted: "Corrupted",
    savesRestore: "Restore",
    savesNoHistory: "No save history",
    savesFooter:
      "Saves are triggered by manual / auto save in-game and sync to the cloud automatically. Manual deletion is not supported for now (continuing to save in-game overwrites the oldest one).",
    savesRemarkTitle: "Edit save remark",
    savesRemarkPlaceholder: "Name this save, e.g. \"Finished Chapter 1\"",
    savesSave: "Save",
    savesAlreadyCurrent: "This is already the current save",
    savesRestoreTitle: "Restore save",
    savesRestoreConfirm:
      "Restore to this save? Next time you launch, the game starts from \"{name}\".",
    savesSwitched: "Switched to this save",
    savesSaved: "Saved",
    ctrlNavTitle: "Controller Settings",
    ctrlSave: "Save",
    ctrlIntro:
      "Adjust mouse / gamepad feel here. Leaving any value empty / 0 follows each game's server default; changes apply to all games.",
    ctrlMouseSensitivity: "Mouse sensitivity",
    ctrlServerDefault: "Server default",
    ctrlMouseHint: "In locked mode, movement distance = physical movement × {n}%",
    ctrlStickSensitivity: "Right stick sensitivity",
    ctrlStickHint:
      "Aim turn speed for FPS / action games. The left stick (movement) is unaffected.",
    ctrlPrimaryClick: "Primary mouse button",
    ctrlLeftPrimary: "Left primary",
    ctrlRightPrimary: "Right primary",
    ctrlPrimaryClickHint:
      "A few games (e.g. some indie titles) fire with right-click by default and need swapping.",
    ctrlResetDefault: "Reset",
    ctrlSavedToast: "Saved, applies next time you launch",
    ctrlResetToast: "Restored to server config",
    savesUploading: "Save is uploading — please try again shortly",
    savesCheckFailed: "Couldn't check save status — please retry",
  },
  play: {
    stageReady: "Ready",
    queueing: "In queue",
    queueJoining: "Joining the queue…",
    queueAheadPrefix: "",
    queueAheadSuffix: "players ahead of you",
    queueEta: "about {n}s",
    cancelReturn: "Cancel and go back",
    back: "Back",
    retry: "Retry",
    goSubscribe: "Upgrade",
    entering: "Entering game",
    vipCta: "Upgrade to VIP to enter now",
    vipPerks: "No queue + HD quality + longer sessions",
    sessionExpiredHint:
      "Sessions usually expire after a long wait — tap \"Retry\" to queue again",
    lineOldUser: "Returning-player queue",
    lineNewbie: "New-player queue",
    lineFast: "Fast-track queue",
    lineSvip: "SVIP queue",
    svipRequired: "This game is SVIP-only. Please upgrade to SVIP first",
    stageConnectingServer: "Connecting to server…",
    stageHandshake: "Handshaking with server…",
    stageP2pNegotiating: "Negotiating P2P channel…",
    stageMediaStreaming: "Streaming media…",
    stageWaitingFrame: "Waiting for video…",
    stageVideoTrack: "Video track received…",
    stageEntered: "Connected",
    stageConnect: "Connection",
    stageConnectedSoon: "Connected — video will appear shortly…",
    allocFailed: "Couldn't allocate a game server",
    allocFailedHttp: "Allocation failed (HTTP {status})",
    reenterHint: " (please exit and re-enter the game)",
    errP2pFailed: "P2P negotiation failed. Please check your network",
    errDisconnected: "Connection lost (code={code}). Please retry",
    errStageFailed: "{stage} failed",
    errStageFailedDetail: "{stage} failed: {detail}",
    errConnectTimeout:
      "Connection timed out — couldn't reach the server. Check your network and retry",
    errQueueCancelled: "The server cancelled your queue. Please retry",
    errKicked:
      "Your account signed in on another device, so this session was disconnected",
    errCannotStart: "Can't start the game right now",
    registerFailed: "Registration failed",
  },
  game: {
    recharge: "Top up",
    tabControl: "Controls",
    tabFrame: "Display",
    tabVoice: "Sound",
    controlScheme: "Control mode",
    hideVirtualKeys: "Hide virtual keys",
    mouseMode: "Mouse mode",
    mouseModeTouchpad: "Touchpad",
    mouseModeDrag: "Drag",
    mouseModeDirect: "Direct",
    mouseSensitivity: "Mouse sensitivity {n}%",
    rightStickMode: "Right stick mode",
    joyModeFixed: "Fixed",
    joyModeHalf: "Half screen",
    joyModeFull: "Full screen",
    joyModeDrag: "Drag",
    stickSensitivity: "Stick sensitivity {n}%",
    customLayout: "Custom key layout",
    editLayout: "Edit layout",
    frameComingSoon:
      "Display scaling / quality switching is coming in a later version.",
    gameSound: "Game sound",
    schemeFollow: "Follow game",
    schemeGamepad: "Gamepad",
    schemeKeyMouse: "Keyboard & mouse",
    schemeTouch: "Touch",
    schemeSwitchedGamepad: "Switched to gamepad",
    schemeSwitchedKeyMouse: "Switched to keyboard & mouse",
    schemeSwitchedFollow: "Restored to follow game",
    modifierTitle: "Modifiers",
    modifierNeedUnlock: "This modifier must be purchased to unlock",
    usageTitle: "Instructions",
    usageEmpty: "No instructions yet",
    gotIt: "Got it",
    close: "Close",
    noModifiers: "No modifiers",
    locked: "Locked",
    needUnlock: "Purchase to unlock",
    remaining: "{d} left",
    use: "Use",
    quitTitle: "Quit game",
    quitConfirm: "Quit the current game?",
    quitConfirmBtn: "Quit",
    quitCancelBtn: "Keep playing",
    joyNameFixed: "Fixed",
    joyNameHalfZone: "Half-screen zone",
    joyNameFullFollow: "Full-screen follow",
    joyNameDrag: "Drag",
    padConnected: "🎮 Gamepad connected, virtual keys hidden",
    padDisconnected: "Gamepad disconnected, virtual keys restored",
    rightStickToast: "Right stick: {name}",
    noEditableLayout: "No editable key layout",
    timeUsedUp: "Play time used up",
    addTimeSuccess: "Time added",
    submitted: "Submitted",
    minutesShort: "{n} min",
    addTime: "Add time",
    expand: "Expand",
    collapse: "Collapse",
    physicalPadConnected: "Physical gamepad connected",
    unmute: "Unmute",
    mute: "Mute",
    fullscreen: "Fullscreen",
    summonKeyboard: "Show keyboard",
    sensoryMode: "Motion controls",
    idleWarnTip: "No activity detected. Exiting the game in {n}s.",
    idleContinue: "Continue",
    nsInputTitle: "Game needs text input",
    nsInputHint: "Type text, then confirm",
    settings: "Settings",
    currentRemaining: "{n} minutes remaining",
    exchangeBeans: "Redeem time with beans",
    useAddTimeCard: "Use add-time card",
  },
  drama: {
    title: "Short Dramas",
    refresh: "Refresh",
    retry: "Retry",
    loadFailed: "Couldn't load dramas. Please try again.",
    completed: "Complete",
    episodes: "{n} episodes",
    noMore: "You're all caught up",
    loadMore: "Load more",
    back: "Back",
    episode: "Episode {n}",
    episodesLabel: "Episodes",
    lockedTitle: "Unlock this episode",
    lockedBody: "Unlock in the host app, or watch a rewarded ad for limited access.",
    unlockEpisode: "Unlock episode",
    watchAd: "Watch ad to unlock",
    adUnlocked: "Episode unlocked — enjoy watching",
    adUnavailable: "No ad is available right now. Please try again later.",
    paymentPending: "Finish payment in the host app. Your access will refresh automatically.",
    paymentUnavailable: "Payment isn't available right now. Please try again later.",
    videoUnavailable: "This video isn't available right now.",
  },
  net: {
    requestFailed: "Request failed",
    errorWithCode: "Error {code}",
    serverError: "Server error {status}",
    timeout: "Request timed out",
    networkError: "Network error",
  },
  controls: {
    selectKeyFirst: "Select a button first",
    saved: "Saved",
    saveFailed: "Save failed",
    selected: "Selected: {name}",
    keyFallback: "button",
    hint: "Drag to reposition; tap to adjust size / opacity / action",
    size: "Size {pct}%",
    opacity: "Opacity {pct}%",
    operation: "Action",
    opPress: "Press",
    opDrag: "Drag",
    opLock: "Lock",
    delete: "Delete",
    addKey: "Add button",
    reset: "Reset",
    save: "Save",
    addHiddenKey: "Add a hidden button",
    noHiddenKeys: "No hidden buttons",
  },
};

export default messages;
